import Debug from '../../helpers/debug.js';
import ora from 'ora';
import AWS from 'aws-sdk';
import Table from 'cli-table';
import descriptions from '../../helpers/descriptions.js';
import { pr, printJSON, themed } from '../../helpers/print.js';
import {
  genCredentialsOnClientApi,
  getTenants,
  getLinkedSources,
  getSupportedFlow,
  getLinkedConnectors,
} from '../../helpers/api.js';
import { buildChunks } from '../../helpers/utils.js';

const debug = Debug('commands:tenants:custom-catalog');

const command = 'custom-catalog';

const desc = 'List tenants with custom catalog in a specific flow';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('tenant', descriptions.options['tenant'].config)
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText);
};

const processTenant = async ({ debug, baseUri, apikey, env, flow, tenant, isV2Flow }) => {
  try {
    // Get all linked connectors/sources of the tuple (env, flow, tenant)
    const linkedConnectors = isV2Flow
      ? await getLinkedConnectors({
        debug,
        baseUri,
        env,
        flow,
        tenant,
        apikey,
      })
      : await getLinkedSources({
        debug,
        baseUri,
        env,
        flow,
        tenant,
        apikey,
      });

    const connectors = [];

    // Generate AWS credentials in order to read the S3 tap folder
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri,
      task: 'field-map-download',
      env,
      tenant,
      flow,
      apikey,
    });

    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    for (const { id, tap } of linkedConnectors) {
      const entityLabel = isV2Flow ? 'connectors' : 'taps';

      const params = {
        Bucket: env,
        Key: `${tenant}/flows/${flow}/${entityLabel}/${isV2Flow ? id : tap}/catalog.json`,
      };

      try {
        await s3.headObject(params).promise();

        connectors.push(isV2Flow ? id : tap);
      } catch (error) {
        // Does not have catalog.json
      }
    }

    return { tenant, connectors };
  } catch (error) {
    console.error(error);
    return { tenant, connectors: [] };
  }
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow } = argv;

  let message;
  let spinner = ora();

  try {
    message = themed(`Retrieving tenants for environment ${themed(env, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const [allTenants, supportedFlow] = await Promise.all([
      getTenants({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
      }),
      getSupportedFlow({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
        flow,
      }),
    ]);

    const isV2Flow = supportedFlow?.version === 2;

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    const tenantsWithCustomFieldMap = [];

    message = themed(`Querying for custom catalogs for flow ${themed(flow, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    // Break the tenants array in to an array of arrays so we can make
    // parallel calls and make it faster
    for (const tenants of buildChunks(allTenants)) {
      const maybeTenantsWithCustomFieldMap = await Promise.all(
        tenants.map((tenant) =>
          processTenant({
            debug,
            baseUri: hg.clientApiBaseUri,
            apikey,
            env,
            flow,
            tenant,
            isV2Flow,
          })
        )
      );

      tenantsWithCustomFieldMap.push(
        ...maybeTenantsWithCustomFieldMap.filter(({ connectors }) => connectors.length > 0)
      );
    }

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    if (json) {
      printJSON(tenantsWithCustomFieldMap);
    } else {
      // generate results table
      const table = new Table({ head: ['Tenant ID', 'Connector IDs'] });
      table.push(...tenantsWithCustomFieldMap.map(({ tenant, connectors }) => [tenant, connectors.join(', ')]));

      // print results
      console.log(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
  }
};

export { command, desc, builder, handler };
